import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './Login.css';

const Login = ({ onLoginSuccess }) => {
  const navigate = useNavigate();
  const [step, setStep] = useState('phone'); // 'phone' or 'otp'
  const [phone, setPhone] = useState('+919167780893');
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [timer, setTimer] = useState(0);

  const BACKEND_URL = '';

  // Handle timer countdown
  useEffect(() => {
    if (timer > 0) {
      const interval = setTimeout(() => setTimer(timer - 1), 1000);
      return () => clearTimeout(interval);
    }
  }, [timer]);

  const handleSendOTP = async (e) => {
    e.preventDefault();
    setError('');
    
    // Validate phone number format
    if (!phone || !/^\+\d{10,15}$/.test(phone)) {
      setError('Please enter a valid phone number (e.g., +919167780893)');
      return;
    }
    
    setLoading(true);

    try {
      const response = await fetch(`${BACKEND_URL}/api/auth/send-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone_number: phone })
      });

      const data = await response.json();

      if (!response.ok) {
        setError(data.detail || 'Failed to send OTP');
        return;
      }

      setMessage(`OTP sent to ${phone}`);
      setStep('otp');
      setTimer(300); // 5 minutes
    } catch (err) {
      setError('Network error: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    setError('');
    
    // Validate OTP format (6-8 digits)
    if (!otp || !/^\d{6,8}$/.test(otp)) {
      setError('Please enter a valid OTP code (6-8 digits)');
      return;
    }
    
    setLoading(true);

    try {
      const response = await fetch(`${BACKEND_URL}/api/auth/verify-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          phone_number: phone,
          otp_code: otp
        })
      });

      const data = await response.json();

      if (!response.ok) {
        setError(data.detail || 'Failed to verify OTP');
        return;
      }

      // Save token
      localStorage.setItem('auth_token', data.token);
      localStorage.setItem('phone_number', phone);

      // Call success callback
      if (onLoginSuccess) {
        onLoginSuccess(data.token);
      }
      
      // Redirect to dashboard
      navigate('/');
    } catch (err) {
      setError('Network error: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleResendOTP = async (e) => {
    e.preventDefault();
    setOtp('');
    await handleSendOTP(e);
  };

  return (
    <div className="login-container">
      <div className="login-box">
        <div className="login-header">
          <h1>🦚 ClawUI</h1>
          <p>AI Agent Control Center</p>
        </div>

        {error && <div className="error-message">{error}</div>}
        {message && <div className="success-message">{message}</div>}

        {step === 'phone' ? (
          <form onSubmit={handleSendOTP}>
            <div className="form-group">
              <label htmlFor="phone">Phone Number</label>
              <input
                id="phone"
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="+919167780893"
                disabled={loading}
                required
              />
              <small>We'll send you a one-time password via WhatsApp</small>
            </div>

            <button type="submit" disabled={loading}>
              {loading ? 'Sending...' : 'Send OTP'}
            </button>
          </form>
        ) : (
          <form onSubmit={handleVerifyOTP}>
            <div className="form-group">
              <label htmlFor="otp">OTP Code</label>
              <input
                id="otp"
                type="text"
                value={otp}
                onChange={(e) => setOtp(e.target.value.slice(0, 6))}
                placeholder="000000"
                maxLength="6"
                disabled={loading}
                required
                autoFocus
              />
              <small>
                {timer > 0 ? (
                  <>Expires in {Math.floor(timer / 60)}:{(timer % 60).toString().padStart(2, '0')}</>
                ) : (
                  <span className="expired">OTP expired</span>
                )}
              </small>
            </div>

            <button type="submit" disabled={loading || timer === 0}>
              {loading ? 'Verifying...' : 'Verify OTP'}
            </button>

            <button
              type="button"
              onClick={handleResendOTP}
              disabled={loading || timer > 0}
              className="secondary-btn"
            >
              {timer > 0 ? `Resend in ${timer}s` : 'Resend OTP'}
            </button>

            <button
              type="button"
              onClick={() => {
                setStep('phone');
                setOtp('');
                setTimer(0);
                setMessage('');
              }}
              className="back-btn"
            >
              Back
            </button>
          </form>
        )}

        <div className="login-footer">
          <p>🔒 Secure login via WhatsApp OTP</p>
        </div>
      </div>
    </div>
  );
};

export default Login;
